// Staff-only admin server functions for «بوصلة د. سارة» (Phase 7).
// Majors audit + reseed, review console state, preflight, publish,
// QA runner, and analytics.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { BOUSALA_MAJORS } from "@/data/bousala-v1.majors";
import { SECTIONS } from "@/data/bousala-v1.config";
import { FLAG_PARAGRAPHS } from "@/data/bousala-v1.report-config";
import { scoreAttempt } from "./scoring";
import type { AnswerMap } from "./types";
import { BOUSALA_REPORT_KIND } from "./registry";

const BOUSALA_SLUG = "bousala-v1";

// ---------- helpers ----------

async function assertStaff(supabase: any, userId: string) {
  const { data: rolesRows } = await supabase
    .from("user_roles").select("role").eq("user_id", userId);
  const roles = (rolesRows ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin") && !roles.includes("assistant")) {
    throw new Error("Forbidden");
  }
}

async function assertAdmin(supabase: any, userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin")) throw new Error("Forbidden: admin only");
}

function hashString(s: string): string {
  // Simple djb2 — enough to detect content changes without a crypto import.
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(16);
}

async function admin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

async function getInstrumentRow(supabase: any) {
  const { data, error } = await supabase
    .from("instruments")
    .select("id, slug, status, config, published_at")
    .eq("slug", BOUSALA_SLUG)
    .single();
  if (error) throw error;
  return data as {
    id: string;
    slug: string;
    status: string;
    published_at: string | null;
    config: any;
  };
}

// ---------- 7.1 Majors audit ----------

export const auditBousalaMajors = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();
    const { data: rows } = await sb
      .from("majors")
      .select("slug, name_ar, cluster, notes_ar, stream_req, prestige")
      .eq("ref", "majors-v1");
    const dbMap = new Map((rows ?? []).map((r: any) => [r.slug, r]));
    const srcMap = new Map(BOUSALA_MAJORS.map((m) => [m.slug, m]));

    const missing = BOUSALA_MAJORS.filter((m) => !dbMap.has(m.slug)).map((m) => ({
      slug: m.slug, name_ar: m.name_ar,
    }));
    const extra = (rows ?? [])
      .filter((r: any) => !srcMap.has(r.slug))
      .map((r: any) => ({ slug: r.slug, name_ar: r.name_ar }));
    const incomplete = (rows ?? [])
      .filter((r: any) => srcMap.has(r.slug))
      .filter((r: any) => !r.notes_ar || !r.cluster)
      .map((r: any) => ({ slug: r.slug, name_ar: r.name_ar, reason: !r.notes_ar ? "missing notes_ar" : "missing cluster" }));

    return {
      required: BOUSALA_MAJORS.length,
      seeded: rows?.length ?? 0,
      missing,
      extra,
      incomplete,
      ok: missing.length === 0 && incomplete.length === 0,
    };
  });

export const reseedBousalaMajors = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const sb = await admin();
    const rows = BOUSALA_MAJORS.map((m) => ({
      slug: m.slug,
      ref: "majors-v1",
      name_ar: m.name_ar,
      cluster: m.cluster,
      stream_req: m.stream_req,
      prestige: m.prestige,
      notes_ar: m.note_ar,
      profile: { riasec: m.riasec, intel: m.intel, trait_mins: m.trait_mins },
    }));
    const { error } = await sb.from("majors").upsert(rows, { onConflict: "slug,ref" });
    if (error) throw error;
    return { ok: true, upserted: rows.length };
  });

// ---------- 7.3 Review console ----------

export type ReviewItem = { id: string; label: string; preview: string; hash: string };
export type ReviewSection = { id: string; label: string; items: ReviewItem[] };

function buildReviewSections(): ReviewSection[] {
  const items: ReviewItem[] = [];
  for (const s of SECTIONS) {
    for (const it of s.items) {
      items.push({
        id: `item:${it.id}`,
        label: `${s.id} · ${it.id}`,
        preview: it.text_ar,
        hash: hashString(it.text_ar),
      });
    }
  }
  const majors: ReviewItem[] = BOUSALA_MAJORS.map((m) => ({
    id: `major:${m.slug}`,
    label: `${m.name_ar} · ${m.cluster}`,
    preview: m.note_ar,
    hash: hashString(m.note_ar),
  }));
  const flags: ReviewItem[] = Object.entries(FLAG_PARAGRAPHS).map(([k, v]) => ({
    id: `flag:${k}`,
    label: k,
    preview: v,
    hash: hashString(v),
  }));
  const pricing: ReviewItem[] = [
    {
      id: "pricing:full",
      label: "التقرير الكامل — $49",
      preview: "فتح التقرير الكامل مقابل 49 دولار: كل التخصصات، المهارات، القيم، طرق التعلم، وخطة الـ90 يوماً.",
      hash: hashString("full:49"),
    },
    {
      id: "pricing:premium",
      label: "الكامل + جلسة تفسير — $99",
      preview: "كل ما في التقرير الكامل + جلسة فيديو مع د. سارة لمناقشة النتائج معك ومع أهلك.",
      hash: hashString("premium:99"),
    },
  ];
  return [
    { id: "items", label: "بنود الاختبار", items },
    { id: "majors", label: "التخصصات", items: majors },
    { id: "flags", label: "فقرات العلامات", items: flags },
    { id: "pricing", label: "نسخة التسعير", items: pricing },
  ];
}

type ApprovalRow = { section: string; item_id: string; hash: string; approved_by: string; approved_at: string };

function readLog(config: any): ApprovalRow[] {
  const log = config?.review_log;
  return Array.isArray(log) ? (log as ApprovalRow[]) : [];
}

export const getBousalaReviewState = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();
    const inst = await getInstrumentRow(sb);
    const sections = buildReviewSections();
    const log = readLog(inst.config);

    const approvedMap = new Map<string, ApprovalRow>();
    for (const row of log) approvedMap.set(`${row.section}::${row.item_id}`, row);

    const withStatus = sections.map((sec) => ({
      ...sec,
      items: sec.items.map((it) => {
        const rec = approvedMap.get(`${sec.id}::${it.id}`);
        const approved = !!rec && rec.hash === it.hash;
        return {
          ...it,
          approved,
          stale: !!rec && rec.hash !== it.hash,
          approved_at: rec?.approved_at ?? null,
        };
      }),
    }));

    const totals = withStatus.map((s) => ({
      id: s.id,
      label: s.label,
      total: s.items.length,
      approved: s.items.filter((i) => i.approved).length,
    }));

    return {
      status: inst.status,
      published_at: inst.published_at,
      sections: withStatus,
      totals,
    };
  });

const approveSchema = z.object({
  section: z.string().min(1),
  item_id: z.string().min(1),
  hash: z.string().min(1),
});

async function mutateLog(mutator: (log: ApprovalRow[]) => ApprovalRow[]) {
  const sb = await admin();
  const inst = await getInstrumentRow(sb);
  const log = readLog(inst.config);
  const nextLog = mutator(log);
  const nextConfig = { ...(inst.config ?? {}), review_log: nextLog };
  const { error } = await sb.from("instruments").update({ config: nextConfig }).eq("id", inst.id);
  if (error) throw error;
  return { ok: true };
}

export const approveReviewItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => approveSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertStaff(context.supabase, context.userId);
    const now = new Date().toISOString();
    return mutateLog((log) => {
      const filtered = log.filter((r) => !(r.section === data.section && r.item_id === data.item_id));
      filtered.push({ section: data.section, item_id: data.item_id, hash: data.hash, approved_by: context.userId, approved_at: now });
      return filtered;
    });
  });

export const unapproveReviewItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => z.object({ section: z.string(), item_id: z.string() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertStaff(context.supabase, context.userId);
    return mutateLog((log) =>
      log.filter((r) => !(r.section === data.section && r.item_id === data.item_id)),
    );
  });

export const approveAllReviewItems = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context.supabase, context.userId);
    const sections = buildReviewSections();
    const now = new Date().toISOString();
    const userId = context.userId;
    return mutateLog(() => {
      const next: ApprovalRow[] = [];
      for (const sec of sections) {
        for (const it of sec.items) {
          next.push({
            section: sec.id,
            item_id: it.id,
            hash: it.hash,
            approved_by: userId,
            approved_at: now,
          });
        }
      }
      return next;
    });
  });

// ---------- 7.4 QA runner + preflight + publish ----------

function buildFixtureAnswers(kind: "mid" | "min" | "max"): AnswerMap {
  const out: AnswerMap = {};
  const val = kind === "mid" ? 3 : kind === "min" ? 1 : 5;
  for (const s of SECTIONS) {
    for (const it of s.items) {
      let v: any;
      if (it.type === "likert5" || it.type === "importance5") v = val;
      else if (it.type === "scenario" || it.type === "pair") v = it.options?.[0]?.id ?? "";
      else if (it.type === "iq") v = it.correct ?? it.options?.[0]?.id ?? "";
      else if (it.type === "top3") v = [];
      else continue;
      out[it.id] = { v, ms: 2500 };
    }
  }
  return out;
}

function runFixtures() {
  const results: { name: string; passed: boolean; note?: string }[] = [];

  // F1 — mid values yield a valid score
  try {
    const r = scoreAttempt(buildFixtureAnswers("mid"), { stream: "علمي" });
    if ("error" in r) throw new Error("incomplete: " + r.missing.join(","));
    results.push({ name: "F1_mid_scores", passed: true });
  } catch (e) {
    results.push({ name: "F1_mid_scores", passed: false, note: String(e) });
  }

  // F2 — extremes don't blow up
  for (const k of ["min", "max"] as const) {
    try {
      const r = scoreAttempt(buildFixtureAnswers(k), { stream: "علمي" });
      if ("error" in r) throw new Error("incomplete");
      results.push({ name: `F2_${k}`, passed: true });
    } catch (e) {
      results.push({ name: `F2_${k}`, passed: false, note: String(e) });
    }
  }

  // F3 — determinism
  try {
    const a = scoreAttempt(buildFixtureAnswers("mid"), { stream: "علمي" });
    const b = scoreAttempt(buildFixtureAnswers("mid"), { stream: "علمي" });
    const same = JSON.stringify(a) === JSON.stringify(b);
    results.push({ name: "F3_determinism", passed: same });
  } catch (e) {
    results.push({ name: "F3_determinism", passed: false, note: String(e) });
  }

  // F4 — flag paragraphs count present
  results.push({
    name: "F4_flag_paragraphs",
    passed: Object.keys(FLAG_PARAGRAPHS).length >= 5,
  });

  // F5 — item bank sizes
  try {
    const total = SECTIONS.reduce((n, s) => n + s.items.length, 0);
    results.push({ name: "F5_item_count", passed: total > 0, note: `items=${total}` });
  } catch (e) {
    results.push({ name: "F5_item_count", passed: false, note: String(e) });
  }

  const passed = results.every((r) => r.passed);
  return { passed, results };
}

export const runQaFixtures = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context.supabase, context.userId);
    const out = runFixtures();
    const sb = await admin();
    await sb.from("qa_runs").insert({
      kind: "bousala-v1",
      passed: out.passed,
      results: out,
      ran_by: context.userId,
    });
    return out;
  });

export const preflightBousala = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();

    // Review
    const inst = await getInstrumentRow(sb);
    const sections = buildReviewSections();
    const log = readLog(inst.config);
    const approvedMap = new Map<string, ApprovalRow>();
    for (const row of log) approvedMap.set(`${row.section}::${row.item_id}`, row);
    let total = 0;
    let approved = 0;
    for (const s of sections) {
      for (const it of s.items) {
        total++;
        const rec = approvedMap.get(`${s.id}::${it.id}`);
        if (rec && rec.hash === it.hash) approved++;
      }
    }
    const review = { total, approved, ok: approved === total };

    // Fixtures — latest qa_runs
    const { data: lastRun } = await sb
      .from("qa_runs")
      .select("passed, ran_at")
      .eq("kind", "bousala-v1")
      .order("ran_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    const fixtures = {
      last_run_at: lastRun?.ran_at ?? null,
      passed: !!lastRun?.passed,
      ok: !!lastRun?.passed,
    };

    // Majors
    const { data: majorRows } = await sb
      .from("majors")
      .select("slug, notes_ar")
      .eq("ref", "majors-v1");
    const seeded = majorRows?.length ?? 0;
    const majorsOk =
      seeded >= BOUSALA_MAJORS.length &&
      (majorRows ?? []).every((r: any) => !!r.notes_ar);
    const majors = { required: BOUSALA_MAJORS.length, seeded, ok: majorsOk };

    // Pricing — read from instruments.config
    const pricingCfg = inst.config?.pricing?.tiers ?? {};
    const fullPrice = Number(pricingCfg?.full?.price ?? 0);
    const premiumPrice = Number(pricingCfg?.premium?.price ?? 0);
    const pricing = {
      full: fullPrice,
      premium: premiumPrice,
      ok: fullPrice > 0 && premiumPrice > 0,
    };

    const ok = review.ok && fixtures.ok && majors.ok && pricing.ok;
    return {
      status: inst.status,
      published_at: inst.published_at,
      review,
      fixtures,
      majors,
      pricing,
      ok,
    };
  });

export const publishBousala = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const sb = await admin();
    const inst = await getInstrumentRow(sb);
    const now = new Date().toISOString();
    const { error } = await sb
      .from("instruments")
      .update({ status: "live", published_at: now })
      .eq("id", inst.id);
    if (error) throw error;
    return { ok: true, published_at: now };
  });

// ---------- 7.5 Analytics ----------

export const getBousalaAnalytics = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();
    const inst = await getInstrumentRow(sb);

    const since30 = new Date(Date.now() - 30 * 86400000).toISOString();

    const [{ data: attemptsAll }, { data: attempts30 }] = await Promise.all([
      sb.from("attempts").select("id, status, tier, completed_at, started_at").eq("instrument_id", inst.id),
      sb.from("attempts").select("id, status, tier, completed_at, started_at").eq("instrument_id", inst.id).gte("started_at", since30),
    ]);

    const funnel = (rows: any[] | null) => {
      const list = rows ?? [];
      return {
        started: list.length,
        completed: list.filter((a) => a.status === "scored" || a.status === "report_ready").length,
        report_ready: list.filter((a) => a.status === "report_ready").length,
      };
    };

    const tierCounts = (rows: any[] | null) => {
      const list = rows ?? [];
      return {
        free: list.filter((a) => (a.tier ?? "free") === "free").length,
        full: list.filter((a) => a.tier === "full").length,
        premium: list.filter((a) => a.tier === "premium").length,
      };
    };

    // Purchases
    const { data: purchases } = await sb
      .from("purchases")
      .select("amount_cents, currency, tier, environment, created_at")
      .order("created_at", { ascending: false });
    const revenueByTier: Record<string, number> = { full: 0, premium: 0, other: 0 };
    const revenueByEnv: Record<string, number> = { sandbox: 0, live: 0 };
    for (const p of purchases ?? []) {
      const cents = Number((p as any).amount_cents ?? 0);
      const tier = String((p as any).tier ?? "");
      const env = String((p as any).environment ?? "sandbox");
      revenueByTier[tier === "full" || tier === "premium" ? tier : "other"] += cents;
      revenueByEnv[env === "live" ? "live" : "sandbox"] += cents;
    }

    // Sessions from premium — service is joined via services.slug
    const { data: interpService } = await sb
      .from("services")
      .select("id")
      .eq("slug", "bousala-interpretation")
      .maybeSingle();
    const { data: interpBookings } = interpService?.id
      ? await sb
          .from("bookings")
          .select("id, scheduled_at, created_at")
          .eq("service_id", interpService.id)
          .order("created_at", { ascending: false })
          .limit(200)
      : { data: [] as any[] };

    return {
      funnel_all: funnel(attemptsAll),
      funnel_30: funnel(attempts30),
      tiers_all: tierCounts(attemptsAll),
      tiers_30: tierCounts(attempts30),
      revenue_cents_by_tier: revenueByTier,
      revenue_cents_by_env: revenueByEnv,
      interpretation_sessions: interpBookings?.length ?? 0,
    };
  });

// ---------- 7.x Attempts browser ----------

const attemptsFilterSchema = z
  .object({
    status: z.enum(["all", "in_progress", "completed", "scored", "report_ready"]).optional(),
    tier: z.enum(["all", "free", "full", "premium"]).optional(),
    search: z.string().max(120).optional(),
    includeTest: z.boolean().optional(),
    language: z.enum(["all", "ar", "en"]).optional(),
    page: z.number().int().min(1).optional(),
    pageSize: z.number().int().min(1).max(100).optional(),
  })
  .optional();

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const listBousalaAttempts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => attemptsFilterSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();
    const inst = await getInstrumentRow(sb);

    const f = data ?? {};
    const page = Math.max(1, f.page ?? 1);
    const pageSize = Math.min(100, Math.max(1, f.pageSize ?? 50));
    const from = (page - 1) * pageSize;
    const empty = { rows: [] as any[], total: 0, page, pageSize };

    const searchTerm = (f.search ?? "").trim();
    let searchClientIds: string[] | null = null;
    if (searchTerm && !UUID_RE.test(searchTerm)) {
      // Keep the id list small: each id adds ~40 chars to the PostgREST URL and
      // large lists blow past the server's header size limit (fetch failed).
      const { data: matched } = await sb
        .from("profiles")
        .select("id")
        .ilike("full_name", `%${searchTerm.replace(/[%_]/g, "")}%`)
        .order("created_at", { ascending: false })
        .limit(120);
      searchClientIds = (matched ?? []).map((p: any) => p.id as string);
      if (searchClientIds.length === 0) return empty;
    }

    let q = sb
      .from("attempts")
      .select("id, client_id, status, tier, confidence, started_at, completed_at, is_test, language", { count: "exact" })
      .eq("instrument_id", inst.id)
      .order("started_at", { ascending: false })
      .range(from, from + pageSize - 1);
    if (f.status && f.status !== "all") q = q.eq("status", f.status);
    if (f.tier && f.tier !== "all") q = q.eq("tier", f.tier);
    if (f.includeTest === false) q = q.eq("is_test", false);
    if (f.language && f.language !== "all") q = q.eq("language", f.language);
    if (searchTerm && UUID_RE.test(searchTerm)) q = q.eq("id", searchTerm);
    else if (searchClientIds) q = q.in("client_id", searchClientIds);
    const { data: attempts, error, count } = await q;
    if (error) throw error;
    const rows = attempts ?? [];
    const total = count ?? 0;
    if (rows.length === 0) return { rows: [], total, page, pageSize };

    const clientIds = Array.from(new Set(rows.map((r: any) => r.client_id).filter(Boolean)));
    const attemptIds = rows.map((r: any) => r.id);

    const [{ data: profs }, { data: reps }] = await Promise.all([
      sb.from("profiles").select("id, full_name").in("id", clientIds),
      sb.from("reports").select("attempt_id").in("attempt_id", attemptIds).eq("kind", BOUSALA_REPORT_KIND),
    ]);
    const nameById = new Map((profs ?? []).map((p: any) => [p.id, p.full_name as string | null]));
    const reportedSet = new Set((reps ?? []).map((r: any) => r.attempt_id));

    const enriched = rows.map((r: any) => ({
      id: r.id as string,
      client_id: r.client_id as string | null,
      client_name: (nameById.get(r.client_id) ?? null) as string | null,
      status: r.status as string,
      tier: (r.tier ?? "free") as "free" | "full" | "premium",
      confidence: r.confidence as "high" | "medium" | "low" | null,
      started_at: r.started_at as string,
      completed_at: r.completed_at as string | null,
      is_test: Boolean(r.is_test),
      has_report: reportedSet.has(r.id),
      language: (r.language === "en" ? "en" : "ar") as "ar" | "en",
    }));
    return { rows: enriched, total, page, pageSize };
  });

export const getBousalaAttemptAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => z.object({ attemptId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();
    const inst = await getInstrumentRow(sb);

    const { data: attempt, error } = await sb
      .from("attempts")
      .select("id, client_id, status, tier, confidence, started_at, completed_at, is_test, paid_at")
      .eq("id", data.attemptId)
      .eq("instrument_id", inst.id)
      .maybeSingle();
    if (error) throw error;
    if (!attempt) throw new Error("Attempt not found");

    const [{ data: profile }, { data: report }] = await Promise.all([
      attempt.client_id
        ? sb.from("profiles").select("id, full_name").eq("id", attempt.client_id).maybeSingle()
        : Promise.resolve({ data: null }),
      sb
        .from("reports")
        .select("id, generated_at, updated_at")
        .eq("attempt_id", attempt.id)
        .eq("kind", BOUSALA_REPORT_KIND)
        .is("deleted_at", null)
        .maybeSingle(),
    ]);

    return {
      id: attempt.id as string,
      client_id: attempt.client_id as string | null,
      client_name: (profile?.full_name ?? null) as string | null,
      status: attempt.status as string,
      tier: (attempt.tier ?? "free") as "free" | "full" | "premium",
      confidence: attempt.confidence as "high" | "medium" | "low" | null,
      started_at: attempt.started_at as string,
      completed_at: attempt.completed_at as string | null,
      paid_at: attempt.paid_at as string | null,
      is_test: Boolean(attempt.is_test),
      has_report: Boolean(report?.id),
      report_generated_at: (report?.generated_at ?? null) as string | null,
      report_updated_at: (report?.updated_at ?? null) as string | null,
    };
  });